import React, { useState, useEffect } from 'react';
import BeforeAfterSlider from './BeforeAfterSlider';

function Gallery() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'algae_control', label: 'Algae Control' },
    { value: 'fountain_maintenance', label: 'Fountain Maintenance' },
    { value: 'vegetation_management', label: 'Vegetation Management' },
    { value: 'water_quality', label: 'Water Quality' },
    { value: 'storm_drainage', label: 'Storm Drainage' },
  ];

  useEffect(() => {
    fetchProjects();
  }, [selectedCategory]);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = selectedCategory === 'all'
        ? '/api/v1/projects'
        : `/api/v1/projects?category=${selectedCategory}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <section id="gallery" className="bg-slate-50 py-8 md:py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-900 text-center mb-4">
          Our Work
        </h2>
        <p className="text-xl text-brand-700 text-center mb-8 max-w-2xl mx-auto">
          See the difference professional aquatic management makes
        </p>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-4 py-2 rounded-full font-medium transition-colors min-h-[44px] ${
                selectedCategory === category.value
                  ? 'bg-water-600 text-white'
                  : 'bg-slate-100 text-brand-700 hover:bg-slate-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-water-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-brand-600">Loading projects...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchProjects}
              className="mt-4 bg-water-600 text-white px-4 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Projects Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-slate-50 rounded-lg overflow-hidden shadow-md border-2 border-brand-100 hover:border-water-400 transition-all cursor-pointer group"
                onClick={() => openModal(project)}
              >
                {/* Thumbnail with before/after preview */}
                {project.before_image_url && project.after_image_url ? (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.after_image_url}
                      alt={project.title}
                      width="400"
                      height="192"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white font-medium">Click to compare</span>
                    </div>
                    <div className="absolute top-2 right-2 bg-nature-600 text-white text-xs font-bold px-2 py-1 rounded">
                      Click to Compare Before & After
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-water-100 to-brand-100 h-48 flex items-center justify-center">
                    <span className="text-5xl">🖼️</span>
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-water-600 bg-water-100 px-2 py-1 rounded">
                      {project.category_display_name}
                    </span>
                    <span className="text-xs font-medium text-nature-600">Featured</span>
                  </div>
                  <h3 className="font-bold text-brand-900 mb-2">{project.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && projects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-brand-600">Images Loading...</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-lg text-brand-700 mb-4">
            Ready to transform your pond or lake?
          </p>
          <a
            href="tel:+17272773366"
            className="inline-block bg-nature-600 hover:bg-nature-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 min-h-[48px]"
          >
            Get Your Free Consultation
          </a>
        </div>
      </div>

      {/* Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-xl font-bold text-brand-900">{selectedProject.title}</h3>
                <span className="text-sm text-water-600">{selectedProject.category_display_name}</span>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              {selectedProject.before_image_url && selectedProject.after_image_url ? (
                <BeforeAfterSlider
                  beforeImage={selectedProject.before_image_url}
                  afterImage={selectedProject.after_image_url}
                  title={selectedProject.title}
                />
              ) : (
                <div className="bg-gradient-to-br from-water-100 to-brand-100 aspect-video flex items-center justify-center rounded-lg">
                  <span className="text-5xl">🖼️</span>
                </div>
              )}

              <p className="mt-4 text-slate-700 leading-relaxed">{selectedProject.description}</p>

              {selectedProject.completed_at && (
                <p className="mt-2 text-sm text-slate-500">
                  Completed: {new Date(selectedProject.completed_at).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-slate-50">
              <p className="text-center text-brand-700 mb-3">
                Want similar results for your pond or lake?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:+17272773366"
                  className="inline-flex items-center justify-center bg-water-600 hover:bg-water-700 text-white font-bold py-3 px-6 rounded-lg transition-colors min-h-[48px]"
                >
                  <span className="mr-2">📞</span>
                  Call Joe
                </a>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    closeModal();
                    setTimeout(() => {
                      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="inline-flex items-center justify-center bg-nature-600 hover:bg-nature-700 text-white font-bold py-3 px-6 rounded-lg transition-colors min-h-[48px]"
                >
                  <span className="mr-2">✉️</span>
                  Email for Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Gallery;
